import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  DateTemplate,
  DropdownBoundTemplate,
  DropdownTemplate,
  TimeTemplate,
} from "@ryde/controls";
import {
  BOOKING_DATE_TIME_UPDATE_FORM_FIELDS,
  DirectionEnum,
  DIRECTIONS_A_B,
  requiredRuleStringField,
  systemSettingsDtoAtom,
  useFormats,
} from "@ryde/shared";

import { DrawerFormWrapperStyled } from "../../drawer";
import {
  BulkCreateBookingsFormBlockStyled,
  BulkCreateBookingsTimeBlockStyled,
  useBulkBookingDateTimeForm,
} from "../bulk-bookings-components";
import { DirectionScopeNotice } from "./direction-scope-notice";

interface IBulkUpdateBookingsDateTimeFormProps {
  direction: DirectionEnum;
  matchingCount: number;
  selectedCount: number;
}

export const BulkUpdateBookingsDateTimeForm: FC<
  IBulkUpdateBookingsDateTimeFormProps
> = ({ direction, matchingCount, selectedCount }) => {
  const { t } = useTranslation();
  const { timeFormat } = useFormats();
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const { disableShiftsDropdown } = systemSettingsDto?.metadata ?? {};
  const { onShiftSelect } = useBulkBookingDateTimeForm();

  return (
    <DrawerFormWrapperStyled>
      <DirectionScopeNotice
        direction={direction}
        matchingCount={matchingCount}
        selectedCount={selectedCount}
      />
      <BulkCreateBookingsFormBlockStyled $paddingTop="24px">
        {!disableShiftsDropdown && (
          <DropdownBoundTemplate
            name={BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.shiftId}
            labelTranslated={t("booking.formLabel.shiftId")}
            formAttributes={{
              dropItemsSource: "shift",
              dropItemsDisableAdd: true,
            }}
            onSelect={onShiftSelect}
          />
        )}
        <DropdownTemplate
          name={BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.direction}
          labelTranslated={t("booking.bulk.applyOnlyToDirection")}
          formAttributes={{ selectItems: DIRECTIONS_A_B }}
          formValidation={requiredRuleStringField}
        />
        <BulkCreateBookingsTimeBlockStyled>
          <DateTemplate
            name={BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.date}
            labelTranslated={t("general.DATE")}
            // Without this the control fills itself with today on mount, and every run would move
            // the bookings to today instead of leaving each on its own day.
            formAttributes={{ allowEmptyDate: true }}
          />
          <TimeTemplate
            name={BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.time}
            timeFormat={timeFormat}
            labelTranslated={t("booking.formLabel.time")}
            formValidation={requiredRuleStringField}
          />
        </BulkCreateBookingsTimeBlockStyled>
      </BulkCreateBookingsFormBlockStyled>
    </DrawerFormWrapperStyled>
  );
};
